/**
 * The quarter-hour grid that logged time lives on.
 *
 * `createTimeEntrySchema` refuses anything off it, so every control that
 * offers to change a number of minutes has to land on it. These constants and
 * `stepMinutes` are the single statement of that rule; the schema imports them
 * rather than repeating the numbers, because a grid defined twice is a grid
 * that eventually disagrees with itself.
 *
 * Note this is about **logged** minutes. `reviewTimeEntrySchema.billableMinutes`
 * deliberately has no such rule - an owner may bill any number of minutes - so
 * the review screen's stepper is a different thing and does not use this.
 */

/** One press of the stepper, and the granularity the schema enforces. */
export const MINUTES_STEP = 15;

/** The shortest entry worth logging. */
export const MINUTES_MIN = 15;

/** Sixteen hours. Longer than any honest day, short enough to catch a typo. */
export const MINUTES_MAX = 960;

/**
 * The next value up or down the grid.
 *
 * **Step *to* the next multiple, not *by* fifteen.** Adding fifteen is only
 * correct from a value already on the grid, and nothing guarantees that: the
 * field between the buttons is typeable, so 70 is a value somebody can be
 * sitting on. Adding fifteen to 70 gives 85, then 100, and the buttons
 * labelled "Fifteen minutes more" and "Fifteen minutes less" can never reach
 * a number the form will accept - the only way out was to retype the field.
 * `docs/FINDINGS.md` F26.
 *
 * On the grid the two readings are identical, which is exactly why this went
 * unnoticed: every value the product creates itself is already a multiple.
 *
 * `null` is the empty field. It starts at the minimum in either direction,
 * because there is no "one step down" from nothing.
 */
export function stepMinutes(current: number | null, direction: 1 | -1): number {
  if (current === null || !Number.isFinite(current)) return MINUTES_MIN;

  const next =
    direction > 0
      ? Math.ceil((current + 1) / MINUTES_STEP) * MINUTES_STEP
      : Math.floor((current - 1) / MINUTES_STEP) * MINUTES_STEP;

  return Math.min(MINUTES_MAX, Math.max(MINUTES_MIN, next));
}
